/**
 * RefinementChat - Chat panel for conversational package refinement (WS3).
 *
 * Allows trainers to refine assessment packages via natural language instructions.
 * Displays instruction/response pairs with change diffs.
 * Collapsible panel, only visible when assessment is draft + package exists.
 */

import { useState, useRef, useEffect } from 'react';
import { apiClient } from '../lib/api';
import type { AssessmentPackageResponse, PackageChange } from '../lib/types';

// ============================================================================
// Types
// ============================================================================

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  changes?: PackageChange[];
  timestamp: Date;
  error?: boolean;
}

interface RefinementChatProps {
  assessmentId: string;
  package_: AssessmentPackageResponse;
  onPackageUpdated: (pkg: AssessmentPackageResponse) => void;
}

// ============================================================================
// Quick Suggestions
// ============================================================================

const QUICK_SUGGESTIONS = [
  'Make the persona more challenging',
  'Simplify the competency descriptions',
  'Add more detail to the scenario',
  'Make the rubric criteria more specific',
  'Adjust the difficulty level down',
  'Add a communication skills competency',
];

// ============================================================================
// Component
// ============================================================================

export function RefinementChat({ assessmentId, package_, onPackageUpdated }: RefinementChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (instruction?: string) => {
    const text = instruction ?? input.trim();
    if (!text || isRefining) return;

    // Add user message
    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsRefining(true);

    try {
      const res = await apiClient.refinePackage(assessmentId, {
        instruction: text,
        version: package_.version,
      });

      if (res.response.error || !res.response.data) {
        const errorMsg = res.response.error?.message ?? 'Refinement failed';
        setMessages((prev) => [
          ...prev,
          {
            id: `msg_${Date.now()}_error`,
            role: 'assistant',
            content: errorMsg,
            timestamp: new Date(),
            error: true,
          },
        ]);
        return;
      }

      const { package: updatedPkg, changeDescription, changes } = res.response.data;

      // Add assistant response
      setMessages((prev) => [
        ...prev,
        {
          id: `msg_${Date.now()}_assistant`,
          role: 'assistant',
          content: changeDescription,
          changes,
          timestamp: new Date(),
        },
      ]);

      // Update parent with new package
      onPackageUpdated(updatedPkg);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: `msg_${Date.now()}_error`,
          role: 'assistant',
          content: err instanceof Error ? err.message : 'An unexpected error occurred',
          timestamp: new Date(),
          error: true,
        },
      ]);
    } finally {
      setIsRefining(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 8,
        overflow: 'hidden',
        marginTop: 16,
      }}
    >
      {/* Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'var(--bg-muted)',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text-primary)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Refine Package</span>
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--semantic-info-light)',
              color: 'var(--semantic-info-text)',
              fontWeight: 500,
            }}
          >
            AI Chat
          </span>
          {messages.length > 0 && (
            <span
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              ({messages.filter((m) => m.role === 'user').length} instructions)
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          {isCollapsed ? '+' : '-'}
        </span>
      </button>

      {!isCollapsed && (
        <div style={{ padding: 16 }}>
          {/* Messages */}
          {messages.length > 0 && (
            <div
              style={{
                maxHeight: 400,
                overflowY: 'auto',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      maxWidth: '85%',
                      padding: '10px 14px',
                      borderRadius: 8,
                      fontSize: 13,
                      lineHeight: 1.5,
                      background: msg.error
                        ? 'var(--semantic-error-light, #fee)'
                        : msg.role === 'user'
                          ? 'var(--semantic-info-light)'
                          : 'var(--bg-muted)',
                      color: msg.error
                        ? 'var(--semantic-error-text, #c00)'
                        : 'var(--text-primary)',
                      border: msg.error
                        ? '1px solid var(--semantic-error-border, #fcc)'
                        : 'none',
                    }}
                  >
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>

                    {/* Change diff */}
                    {msg.changes && msg.changes.length > 0 && (
                      <div
                        style={{
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: '1px solid var(--border-subtle)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: 'var(--text-secondary)',
                            marginBottom: 4,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Changes
                        </div>
                        {msg.changes.map((change, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 6,
                              fontSize: 12,
                              marginBottom: 2,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            <span
                              style={{
                                flexShrink: 0,
                                width: 16,
                                height: 16,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 10,
                                fontWeight: 700,
                                borderRadius: 3,
                                color: '#fff',
                                background:
                                  change.type === 'added'
                                    ? '#22c55e'
                                    : change.type === 'removed'
                                      ? '#ef4444'
                                      : '#3b82f6',
                              }}
                            >
                              {change.type === 'added' ? '+' : change.type === 'removed' ? '-' : '~'}
                            </span>
                            <span>
                              <strong style={{ textTransform: 'capitalize' }}>{change.section}</strong>:{' '}
                              {change.description}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                      marginTop: 2,
                      padding: '0 4px',
                    }}
                  >
                    {msg.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Quick suggestions (only show if no messages yet) */}
          {messages.length === 0 && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginBottom: 8,
                }}
              >
                Tell the AI how to modify the package, or try a quick suggestion:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {QUICK_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSubmit(suggestion)}
                    disabled={isRefining}
                    style={{
                      padding: '6px 12px',
                      fontSize: 12,
                      borderRadius: 16,
                      border: '1px solid var(--border-default)',
                      background: 'var(--bg-surface)',
                      color: 'var(--text-secondary)',
                      cursor: isRefining ? 'not-allowed' : 'pointer',
                      opacity: isRefining ? 0.5 : 1,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isRefining) {
                        e.currentTarget.style.background = 'var(--bg-muted)';
                        e.currentTarget.style.borderColor = 'var(--border-strong)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--bg-surface)';
                      e.currentTarget.style.borderColor = 'var(--border-default)';
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input area */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe how to refine the package..."
              disabled={isRefining}
              rows={2}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid var(--border-default)',
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: 13,
                resize: 'vertical',
                minHeight: 44,
                maxHeight: 120,
                fontFamily: 'inherit',
                opacity: isRefining ? 0.6 : 1,
              }}
            />
            <button
              onClick={() => handleSubmit()}
              disabled={isRefining || !input.trim()}
              className="btn btn-primary"
              style={{
                padding: '10px 20px',
                fontSize: 13,
                whiteSpace: 'nowrap',
                minHeight: 44,
              }}
            >
              {isRefining ? 'Refining...' : 'Refine'}
            </button>
          </div>

          {/* Loading indicator */}
          {isRefining && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: 'var(--text-tertiary)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--semantic-info-text)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
              AI is refining the package...
            </div>
          )}

          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: 'var(--text-tertiary)',
            }}
          >
            Press Enter to send, Shift+Enter for new line. Version: {package_.version}
          </div>
        </div>
      )}
    </div>
  );
}
